import DelayedPageLoadingScreen from "@/Components/Navigation/DelayedPageLoadingScreen";
import { AnalyticsContext } from "@/Contexts/AnalyticsContext";
import {
    isShownToLoggedOutMobileApp,
    lazyPage,
    matchPage,
    PageAccess,
    PageMatch,
    preloadLoggedOutPagesWhenIdle,
} from "@/pageRoutes";
import { ErrorScreen } from "@/Pages/Error";
import SimpleError from "@/Pages/SimpleError";
import { Client, Coach } from "@/types/page";
import { PromotionCode } from "@/types/PromotionCode";
import { responseStatus } from "@/utils/apiErrors";
import { homePageFor } from "@/utils/homePage";
import { toAppUrl, toSpaUrl } from "@/utils/hosts";
import {
    forgetEndedSession,
    hasLoggedInMarker,
    identityQuery,
} from "@/utils/identity";
import {
    navigateTo,
    PageParamsContext,
    PageSearchContext,
    subscribeToVisits,
    useVisitCount,
} from "@/utils/navigation";
import { queryClient } from "@/utils/queryClient";
import { trackSpaPageView } from "@/utils/spaAnalytics";
import { savePromotionCode } from "@/utils/usePromoCode";
import { captureUtmParams } from "@/utils/utmParams";
import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import {
    lazy,
    Suspense,
    useContext,
    useDeferredValue,
    useEffect,
    useState,
} from "react";
import { useLocation, useRouter } from "wouter";
import { navigate } from "wouter/use-browser-location";

const JeffLandingHome = lazy(() => import("@/Pages/Home/CareerPlusHome"));

const LoggedInProviders = lazy(() =>
    import("@/Contexts/GlobalStateProviders").then((module) => ({
        default: module.GlobalStateProviders,
    })),
);

const LOGGED_IN_ACCESS: PageAccess[] = ["client", "coach", "user"];

function accessRedirect(
    access: PageAccess,
    user: Client | Coach | null,
    returnTo: string,
): string | null {
    if (access === "public") {
        return null;
    }

    if (access === "guest") {
        return user ? homePageFor(user) : null;
    }

    if (!user) {
        return route(access === "coach" ? "coach.login" : "client.login", {
            redirect: returnTo,
        });
    }

    if (access !== "user" && user.type !== access) {
        return homePageFor(user);
    }

    return null;
}

/*
 * Sends a decoy signup where a real unverified client ends up: guest and coach
 * pages on the verify page, as the server sends a decoy who loads the client
 * dashboard, and logged in pages on the verify page with the page to come back
 * to, as their data's verified guard does.
 */
function decoySignupRedirect(
    access: PageAccess,
    returnTo: string,
): string | null {
    if (access === "public") {
        return null;
    }

    return access === "guest" || access === "coach"
        ? route("verification.notice")
        : route("verification.notice", { redirect: returnTo });
}

/*
 * TanStack does not fetch a query again when a page mounts it after its error
 * took a page to the error screen (retryOnMount stays off until an error reset
 * boundary resets), so a new visit forgets the failed loads nothing shows any
 * more. Queries a mounted component observes are kept, or their invalidations
 * would no longer reach it.
 */
function forgetFailedPageLoads() {
    queryClient.removeQueries({
        predicate: (query) =>
            query.state.status === "error" &&
            query.state.data === undefined &&
            query.getObserversCount() === 0,
    });
}

export default function AppRouter() {
    const [location] = useLocation();
    const router = useRouter();
    // wouter's useSearch runs decodeURI over the query, so URLSearchParams would then decode an escaped "%" a second time.
    const search = router.searchHook(router).replace(/^\?/, "");
    const visitCount = useVisitCount();

    /*
     * Routing off a deferred location and visit keeps the page that is already
     * on screen committed while the next page's chunk downloads, instead of
     * flashing the Suspense fallback in between.
     */
    const deferredLocation = useDeferredValue(location);
    const deferredSearch = useDeferredValue(search);
    const deferredVisitCount = useDeferredValue(visitCount);
    const match = matchPage(deferredLocation);

    useEffect(() => {
        preloadLoggedOutPagesWhenIdle();
    }, []);

    useEffect(() => {
        captureUtmParams();
        trackSpaPageView();
    }, [location, visitCount]);

    // A back or forward step that only changes the query string leaves the path alone, so the effect above misses it.
    useEffect(() => {
        window.addEventListener("popstate", trackSpaPageView);
        return () => window.removeEventListener("popstate", trackSpaPageView);
    }, []);

    useEffect(() => {
        window.addEventListener("popstate", forgetFailedPageLoads);
        const stopForgettingOnVisits = subscribeToVisits(forgetFailedPageLoads);

        return () => {
            window.removeEventListener("popstate", forgetFailedPageLoads);
            stopForgettingOnVisits();
        };
    }, []);

    return (
        <Sentry.ErrorBoundary
            fallback={({ error }) => <AppErrorScreen error={error} />}
        >
            {deferredLocation === "/page-load-error" ? (
                <SimpleError />
            ) : match ? (
                <GuardedPage
                    match={match}
                    location={deferredLocation}
                    search={deferredSearch}
                    visit={deferredVisitCount}
                />
            ) : (
                <UnknownPage />
            )}
        </Sentry.ErrorBoundary>
    );
}

type AppErrorScreenProps = {
    error: unknown;
};

/*
 * Nothing resets AppRouter's own error boundary, because what fails there (the
 * identity, the logged in providers) only loads again on a full page load. So
 * every navigation from its error screen, Go Home and the navbar's links as
 * well as back and forward, reloads the page the browser has just moved to.
 */
function AppErrorScreen({ error }: AppErrorScreenProps) {
    useEffect(() => {
        const reload = () => window.location.reload();
        window.addEventListener("popstate", reload);
        const stopReloadingOnVisits = subscribeToVisits(reload);

        return () => {
            window.removeEventListener("popstate", reload);
            stopReloadingOnVisits();
        };
    }, []);

    return <ErrorScreen errorCode={responseStatus(error) ?? 500} />;
}

type GuardedPageProps = {
    match: PageMatch;
    location: string;
    search: string;
    visit: number;
};

function GuardedPage({ match, location, search, visit }: GuardedPageProps) {
    const isOpenToLoggedOutVisitors =
        match.access === "public" || match.access === "guest";
    const needsIdentity = !isOpenToLoggedOutVisitors || hasLoggedInMarker();
    const identity = useQuery({ ...identityQuery, enabled: needsIdentity });
    const [guestPageShownLoggedOutAt, setGuestPageShownLoggedOutAt] = useState<
        string | null
    >(null);

    // The server clears the marker when the session ends, so an answer cached before then no longer counts on pages open to logged out visitors.
    const currentIdentity = needsIdentity ? identity.data : undefined;
    const isEndedSessionCached =
        !needsIdentity &&
        !!identity.data?.user &&
        identity.fetchStatus === "idle";
    const user = currentIdentity?.user ?? null;
    // Logging in on a guest page (login, register) keeps that page mounted until its own navigation to where the user was going.
    const isLoggingInOnGuestPage =
        match.access === "guest" && guestPageShownLoggedOutAt === location;
    const isWaitingForIdentity =
        needsIdentity &&
        identity.isPending &&
        match.access !== "public" &&
        !isLoggingInOnGuestPage;
    const usesLoggedInProviders =
        LOGGED_IN_ACCESS.includes(match.access) ||
        (match.access === "public" && !!user);
    const isShowingGuestPageLoggedOut =
        match.access === "guest" && !isWaitingForIdentity && !user;
    const isKnownToBeLoggedOut = needsIdentity
        ? identity.isSuccess && !user
        : true;
    const keepsMobileAppOnLogin =
        !!window.isMobileApp &&
        isKnownToBeLoggedOut &&
        !isShownToLoggedOutMobileApp(match.name);
    const returnTo = search ? `${location}?${search}` : location;
    const redirect = isWaitingForIdentity
        ? null
        : currentIdentity?.mustVerifyEmail
          ? decoySignupRedirect(match.access, returnTo)
          : keepsMobileAppOnLogin
            ? route("client.login", { redirect: returnTo })
            : isLoggingInOnGuestPage
              ? null
              : accessRedirect(match.access, user, returnTo);

    useEffect(() => {
        if (isShowingGuestPageLoggedOut) {
            setGuestPageShownLoggedOutAt(location);
        }
    }, [isShowingGuestPageLoggedOut, location]);

    useEffect(() => {
        if (redirect) {
            void navigateTo(redirect, { replace: true });
        }
    }, [redirect]);

    useEffect(() => {
        if (isEndedSessionCached) {
            forgetEndedSession();
        }
    }, [isEndedSessionCached]);

    const Page = lazyPage(match.name);
    const page = (
        <PageParamsContext.Provider value={match.params}>
            <PageSearchContext.Provider value={search}>
                {/* Outside the keyed boundary: a remounted Suspense shows its fallback instead of keeping the current page. */}
                <Suspense fallback={<DelayedPageLoadingScreen />}>
                    <Sentry.ErrorBoundary
                        key={`${visit}:${location}`}
                        fallback={({ error }) => (
                            <ErrorScreen
                                errorCode={responseStatus(error) ?? 500}
                            />
                        )}
                    >
                        <Page />
                    </Sentry.ErrorBoundary>
                </Suspense>
            </PageSearchContext.Provider>
        </PageParamsContext.Provider>
    );

    return isWaitingForIdentity || redirect ? (
        <DelayedPageLoadingScreen />
    ) : usesLoggedInProviders ? (
        <Suspense fallback={<DelayedPageLoadingScreen />}>
            <LoggedInProviders>{page}</LoggedInProviders>
        </Suspense>
    ) : (
        page
    );
}

/*
 * Paths the SPA has no page for. A path Laravel serves itself, such as
 * magic-login, is sent to the app host. Anything else is treated as a Jeff
 * partner landing page code.
 */
function UnknownPage() {
    const { setIsAmplitudeInitializationBlocked } =
        useContext(AnalyticsContext);

    const isLaravelRoute = route().current() !== "dynamic-page";

    const [isFetchingJeffData, setIsFetchingJeffData] = useState(true);

    const fetchJeffData = async () => {
        const code = route().params.dynamicPage;
        try {
            const response = await axios.get<
                Record<string, string> & { promotionData: PromotionCode }
            >(route("jeff-data", code));
            if (typeof response.data !== "object") {
                // Jeff data once came back as HTML, and looping over it froze the page.
                throw new Error("Weird response from Jeff data");
            }
            const newURL = new URL(toSpaUrl(route("home")));
            const existingParams = new URLSearchParams(window.location.search);
            existingParams.forEach((value, key) => {
                newURL.searchParams.set(key, value);
            });
            savePromotionCode(response.data.promotionData);
            Object.entries(response.data).forEach(([key, value]) => {
                if (typeof value === "object" || existingParams.get(key)) {
                    return;
                }
                newURL.searchParams.set(key, existingParams.get(key) ?? value);
            });
            navigate(newURL);
        } catch (error) {
            setIsFetchingJeffData(false);
            if (error instanceof AxiosError && error.response?.status === 404) {
                return;
            }
            throw error;
        } finally {
            setIsAmplitudeInitializationBlocked(false);
        }
    };

    useEffect(() => {
        if (isLaravelRoute) {
            window.location.replace(toAppUrl(window.location.href));
        } else {
            fetchJeffData();
        }
    }, []);

    // Loading the homepage chunk here would race the redirect, and Safari fails an import the unload cancels.
    return isLaravelRoute ? (
        <DelayedPageLoadingScreen />
    ) : (
        <Suspense fallback={null}>
            <JeffLandingHome isFetchingJeffData={isFetchingJeffData} />
        </Suspense>
    );
}
